import { useEffect, useMemo } from "react"
import Center from "components/center"
import Flex from "components/flex"
import CircleIcon1 from "icons/CircleIcon1"
import CircleIcon2 from "icons/CircleIcon2"
import SparkleIcon from "icons/SparkleIcon"
import TagShortcut from "../subcomponents/TagShortcut"
import Select from "../subcomponents/Select"
import { useMutation, useQuery } from "@tanstack/react-query"
import useFlowBuilder from "hooks/useFlowBuilder"
import useLegalDocumentV2 from "hooks/useLegalDocumentV2"
import { useNavigate } from "react-router-dom"
import { otherCourts, TJs, TRTs, TRFs } from "pages/GraphPage/constants"
import { hideSidebarAtom } from "components/sidebar/atoms"
import { useAtom } from "jotai"
import Loading from "components/loading"
import { areaIcons } from "pages/FlowBuilderPage/constants"
import { LegalDocumentFlow } from "types/legalDocument"
import useFreePlan from "hooks/useFreePlan"
import { plansModalAtom } from "pages/BasePage/atoms"

const tags = [
	{
		text: "Petição Inicial",
	},
	{
		text: "Contestação",
	},
	{
		text: "Reclamação Trabalhista",
	},
]

const allCourts = [
	...Object.values(otherCourts),
	...Object.values(TJs),
	...Object.values(TRTs),
	...Object.values(TRFs),
]

const CreateWithAiStep = () => {
	const { listValidFlows } = useFlowBuilder()
	const { createLegalDocument } = useLegalDocumentV2()
	const navigate = useNavigate()
	const [, setHideSidebar] = useAtom(hideSidebarAtom)
	const { data: flows } = useQuery(["listValidFlows"], listValidFlows)
	const { recentlyUsedFlow } = useFlowBuilder()
	const isFreeUser = useFreePlan()
	const [, setIsOpen] = useAtom(plansModalAtom)

	const { data: recentlyUsedFlows } = useQuery(["recentlyUsedFlow"], () =>
		recentlyUsedFlow(),
	)
	console.log(recentlyUsedFlows)
	const createLegalDocumentMutation = useMutation({
		mutationFn: createLegalDocument,
		onSuccess: (data) => {
			navigate(`/document/${data.id}`)
		},
	})

	function onSelectFlow(docFlowId: string) {
		createLegalDocumentMutation.mutate({
			flow: docFlowId,
			courts: allCourts,
			writing_tone: "objetiva",
			optional_attachments: [],
		})
	}

	const groupedData = flows?.reduce<Record<string, typeof flows>>(
		(acc, curr) => {
			const area = curr.area_of_law

			if (!acc[area]) {
				acc[area] = []
			}

			acc[area].push(curr)

			return acc
		},
		{},
	)

	useEffect(() => {
		setHideSidebar(false)
	}, [])

	const tagsElements = useMemo<JSX.Element[] | null>(() => {
		if (!recentlyUsedFlows || !flows) return null

		let flowsToRender: LegalDocumentFlow[] = recentlyUsedFlows.filter(
			(f, i, arr) => arr.findIndex((x) => x.id === f.id) === i,
		)

		if (flowsToRender.length < 3) {
			const needed = 3 - flowsToRender.length
			const defaultFlows: LegalDocumentFlow[] = tags
				.map((tag) => flows.find((flow) => flow.name === tag.text))
				.filter(
					(f) =>
						Boolean(f) &&
						!flowsToRender.some(
							(existing) => existing.id === f?.id,
						),
				)
				.map((f) => f!)
				.slice(0, needed)

			flowsToRender = [...flowsToRender, ...defaultFlows]
		}

		return flowsToRender.map((flow) => (
			<div
				key={flow.id}
				onClick={() => {
					if (isFreeUser && flow.is_premium) {
						setIsOpen(true)
						return
					}
					onSelectFlow(flow.id)
				}}
			>
				<TagShortcut
					icon={areaIcons[flow.area_of_law as keyof typeof areaIcons]}
					text={flow.name}
				/>
			</div>
		))
	}, [recentlyUsedFlows, flows, onSelectFlow])

	if (!flows || createLegalDocumentMutation.isLoading || !recentlyUsedFlows)
		return <Loading />

	return (
		<Center className="w-full h-[calc(100vh-24px)] !font-Red-Hat-Display flex-col bg-[#FCFCFC] ">
			<Flex className="gap-4 relative">
				<SparkleIcon />
				<p className="text-h2 text-[#3D3D3D] font-semibold">
					Crie sua peça com IA
				</p>
				<div className="absolute top-[-80px] left-[-80px]">
					<CircleIcon1 />
				</div>
				<div className="absolute top-[-80px] right-[-90px]">
					<CircleIcon2 />
				</div>
			</Flex>

			<Flex className="my-8 justify-center flex-col">
				<Select
					onSelect={(e) => {
						if (e) {
							onSelectFlow(e)
						}
					}}
					options={groupedData}
					placeholder="Escolha sua peça"
				/>
				<Flex className="flex-wrap gap-2 justify-center w-[600px] z-10 mt-6">
					{tagsElements}
				</Flex>
			</Flex>
		</Center>
	)
}

export default CreateWithAiStep
